import { useQuery } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useMemo } from "react";

import type { ShipmentStopDetails } from "~/shared/schemas/shipment-details-schema.ts";
import { shipmentStopDetailsSchema } from "~/shared/schemas/shipment-details-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

export class ShipmentDetailsNotFoundError extends Error {
	constructor(poNumber: number) {
		super(`No shipment details found for PO ${poNumber}`);
		this.name = "ShipmentDetailsNotFoundError";
	}
}

type UseShipmentDetailsReturn = {
	data: ShipmentStopDetails | undefined;
	isLoading: boolean;
	isError: boolean;
	isNotFound: boolean;
};

export function useShipmentDetails(
	poNumber: number | null | undefined,
): UseShipmentDetailsReturn {
	const enabled = poNumber != null && poNumber > 0;

	const path = useMemo(() => {
		if (!enabled) return "";
		return apiRoutes.SHIPMENTDETAILSBYPO.replace(":poNumber", String(poNumber));
	}, [enabled, poNumber]);

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["shipment-details", poNumber],
		queryFn: async ({ signal }) => {
			const result = await getAndValidate(path, shipmentStopDetailsSchema, {
				signal,
				onError: (err) => {
					if (err instanceof HTTPError && err.response.status === 404) {
						return {
							success: false as const,
							error: "Not Found",
							details: { status: 404 },
						};
					}
					return null;
				},
			});

			if (!result.success) {
				const details = result.details as { status?: number } | undefined;
				if (details?.status === 404) {
					throw new ShipmentDetailsNotFoundError(poNumber ?? 0);
				}
				throw new Error(result.error);
			}

			return result.data;
		},
		enabled,
		staleTime: 0,
		refetchOnMount: "always",
	});

	const isNotFound = isError && error instanceof ShipmentDetailsNotFoundError;

	return { data, isLoading, isError, isNotFound };
}
